import * as THREE from 'three';
import { ANSEL } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Ansel's own effects: his shots of fire bursting on the heroes, the ash his pages leave on a nightmare, the wall of
 * fire across the way, the ring of ash in his last phase and the columns of the burning library. (His embers are the
 * shared orbs, in his colours.) Only vfx primitives and ground glows: nothing is made mid-fight.
 */
const K = WORLD_PER_HEX / 1000;
const TAU = Math.PI * 2;
const GLOW = '#ff9a3a';
const ACCENT = '#ff7a1f';
const PALE = '#ffd0a0';
const SMOKE = '#1c1612';
const ASH = '#5a524c';
const FIRE = new THREE.Color(GLOW);
const ASHC = new THREE.Color('#8a807a');

export const anselFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: '#140d08', smoke: SMOKE },
  clips: { anselEmber: 'ember', anselWall: 'wall' },
  frame: (c, s, e, p, _yaw, h) => {
    const low = c.low;
    const u = e.un;
    if (!u) return;
    // Ash drifting off him; embers off the staff.
    if ((s.tick + e.id) % 5 === 0) c.vfx.burst(p.clone().setY(p.y + h * 0.9), { n: 1, color: ASH, speed: 0.01, up: 0.03, size: 0.3, life: 18, smoke: true, spread: 0.3 });
    // The wall of fire.
    for (const z of s.zones) {
      if (z.kind !== 'anselWall') continue;
      const q = c.at(z.x, z.y, 0.1);
      const r = z.radius * K;
      c.glow(q, FIRE, r * 2.2, 0.5 + ((s.tick + z.id) % 4 < 2 ? 0.15 : 0));
      if ((s.tick + z.id) % 2 === 0) {
        const at = q.clone().add(new THREE.Vector3(Math.sin(s.tick + z.id) * r * 0.5, 0.2, Math.cos(s.tick * 1.7 + z.id) * r * 0.5));
        c.vfx.burst(at, { n: low ? 2 : 5, color: '#ff6a24', speed: 0.02, up: 0.1, size: 0.24, life: 9, gravity: -0.005, spread: r * 0.5 });
        c.vfx.burst(at, { n: 1, color: PALE, speed: 0.01, up: 0.12, size: 0.08, life: 10, gravity: -0.004, spread: r * 0.4 });
      }
      if ((s.tick + z.id) % 5 === 0) c.vfx.burst(q.clone().setY(q.y + 0.9), { n: 1, color: SMOKE, speed: 0.01, up: 0.05, size: 0.7, life: 20, smoke: true, spread: r * 0.4 });
    }
    // The ring of ash in his last phase.
    if (e.phase >= 2) {
      const R = ANSEL.ring.radius * K;
      if ((s.tick + e.id) % 3 === 0) c.vfx.ring(p.clone().setY(p.y + 0.06), R * 0.96, R, ACCENT, 4, 0.6);
      const n = low ? 2 : 5;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * TAU;
        c.vfx.burst(p.clone().add(new THREE.Vector3(Math.cos(a) * R, 0.15, Math.sin(a) * R)), { n: 1, color: i % 2 ? ASH : GLOW, speed: 0.01, up: 0.04, size: i % 2 ? 0.3 : 0.08, life: 12, smoke: i % 2 === 1, gravity: -0.003 });
      }
    }
    // The nightmares under his ash.
    for (const o of s.enemies) {
      if (!((o.ash ?? 0) > 0) || o.hp <= 0) continue;
      const q = c.at(o.x, o.y, 0.05);
      c.glow(q, ASHC, 1.4, 0.35);
      if ((s.tick + o.id) % 4 === 0) c.vfx.burst(q.clone().setY(q.y + 0.6), { n: 1, color: ASH, speed: 0.01, up: 0.02, size: 0.35, life: 14, smoke: true, spread: 0.3 });
    }
  },
  skill: (c, _s, _e, name, p, h) => {
    if (name === 'anselEmber') {
      c.flare(p.clone().setY(p.y + h * 0.6), GLOW, 1.4, 8);
      return true;
    }
    if (name === 'anselWall') {
      c.flare(p.clone().setY(p.y + 0.5), GLOW, 2, 8);
      c.shake(0.12);
      return true;
    }
    return false;
  },
  beam: (c, kind, a, b) => {
    if (kind === 'anselShot') {
      c.vfx.beam(a, b, PALE, 3, 0.04, 0);
      c.vfx.beam(a, b, ACCENT, 5, 0.08, 0.06);
      return true;
    }
    if (kind === 'anselPages') {
      // A thread of ash and burning paper to the nightmare he covers.
      c.vfx.beam(a, b, ASH, 8, 0.05, 0.15);
      c.vfx.burst(b, { n: c.low ? 3 : 7, color: ASH, speed: 0.03, up: 0.03, size: 0.35, life: 16, smoke: true, spread: 0.3 });
      c.vfx.burst(b, { n: c.low ? 2 : 4, color: GLOW, speed: 0.03, up: 0.05, size: 0.07, life: 10, gravity: -0.003 });
      return true;
    }
    if (kind === 'anselWall') {
      // The wall rising: a line of fire along the ground.
      const g1 = a.clone().setY(a.y - 0.7);
      const g2 = b.clone().setY(b.y - 0.5);
      c.vfx.shaft(g1, g2, ACCENT, 10, 0.5);
      c.vfx.beam(g1, g2, PALE, 6, 0.05, 0.1);
      return true;
    }
    return false;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    // `r` comes in world units (times K again the rings shrank to nothing).
    const R = r;
    if (kind === 'anselBurst' || kind === 'anselEmber') {
      // Fire bursting on the heroes: a ring of flame, sparks, smoke.
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, R, ACCENT, 7, 0.9);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: GLOW, speed: 0.09, up: 0.08, size: 0.1, life: 8, gravity: 0.006 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 2 : 5, color: SMOKE, speed: 0.04, up: 0.04, size: 0.5, life: 16, smoke: true, spread: 0.3 });
      if (kind === 'anselEmber') {
        c.flare(p.clone().setY(p.y + 0.5), GLOW, 2.4, 8);
        c.shake(0.2);
      }
      return true;
    }
    if (kind === 'anselColumn') {
      // A column of fire falls on a tower: from the sky into the ground, flash and ash.
      c.vfx.shaft(p.clone().setY(p.y + 9), p.clone().setY(p.y), ACCENT, 12, R * 0.9);
      c.vfx.beam(p.clone().setY(p.y + 9), p.clone().setY(p.y), PALE, 8, 0.12, 0);
      c.vfx.ring(p.clone().setY(p.y + 0.06), 0.2, R, ACCENT, 12, 1);
      c.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 8 : 20, color: GLOW, speed: 0.12, up: 0.14, size: 0.12, life: 14, gravity: 0.006 });
      c.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 4 : 10, color: SMOKE, speed: 0.05, up: 0.06, size: 0.9, life: 26, smoke: true, spread: 0.4 });
      c.flare(p.clone().setY(p.y + 1.2), GLOW, 5, 14);
      c.shake(0.5);
      return true;
    }
    if (kind === 'anselLibrary') {
      if (e) c.force(e.id, 'library');
      c.flare(p.clone().setY(p.y + 1), ACCENT, 4, 12);
      c.shake(0.7);
      return true;
    }
    if (kind === 'anselRing') {
      c.vfx.ring(p.clone().setY(p.y + 0.06), R * 0.6, R, GLOW, 6, 0.6);
      return true;
    }
    if (kind === 'anselAshGone') {
      c.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 3 : 7, color: ASH, speed: 0.05, up: 0.03, size: 0.35, life: 14, smoke: true, spread: 0.3 });
      return true;
    }
    return false;
  },
};
